<template>
    <DataTable
        :header="$t('offerList')"
        :fields="fields"
        :items="parentComponent.items"
        :loading="parentComponent.loading"
        :error="parentComponent.error"
        :total-rows="parentComponent.requestData.max"
        :current-page.sync="parentComponent.tableParams.page"
        :per-page.sync="parentComponent.tableParams.perPage"
        :sort-by.sync="parentComponent.tableParams.sort"
        :sort-direction.sync="parentComponent.tableParams.order"
        no-sort-reset
        no-local-sorting
    >
        <template #cell(product)="{ item }">
            <div class="d-flex flex-column gap-2">
                <template v-if="getProductIds(item).length">
                    <div v-for="productId in getProductIds(item)" :key="productId">
                        {{ productId }}
                    </div>
                </template>
                <span v-else>-</span>
                <div class="info text-secondary font-size-12">
                    {{ item.offerId || item.id }}
                </div>
            </div>
        </template>

        <template #cell(name)="{ item }">
            <div class="d-flex">
                <ImageLoader :src="item.image" alt="" size="100" image-height="auto" class="mr-2 flex-shrink-0" />
                <div class="d-flex flex-column gap-2">
                    <div class="title">
                        {{ item.name }}
                    </div>
                    <div v-if="Array.isArray(item.categories) && item.categories.length" class="info text-secondary font-size-12">
                        {{ item.categories.join(', ') }}
                    </div>
                </div>
            </div>
        </template>

        <template #cell(feed)="{ item }">
            <div class="d-flex flex-column gap-2">
                <span>{{ item.feed }}</span>
                <span v-if="item.campaign" class="text-secondary">
                    {{ item.campaign.name }}
                </span>
            </div>
        </template>

        <template #cell(price)="{ item }">
            <div class="nowrap">
                {{ $format.currency(item.price) }}
            </div>
        </template>

        <template #cell(type)="{ item }">
            {{ getTypeLabel(item.type) }}<br>
            <template v-if="item.format">{{ item.format }}</template>
        </template>

        <template #cell(hidden)="{ item }">
            <BadgeHidden :value="item.hidden" />
        </template>

        <template #cell(active)="{ item }">
            <div class="d-flex flex-column gap-2">
                <div><BadgeBoolean :value="item.active" :true-value-text="$t('available2')" :false-value-text="$t('unavailable2')" /></div>
                <div v-if="item.lastAvailabilityChangedAt" class="info text-secondary font-size-12">
                    {{ $format.dateAndHour(item.lastAvailabilityChangedAt, 'yyyy-MM-dd') }}
                </div>
            </div>
        </template>
        <template #cell(risk)="{ item }">
            <Tooltip v-if="getRiskHtml(item)" :html="getRiskHtml(item)" :width="400">
                {{ getRiskValue(item) }}
            </Tooltip>
            <span v-else>
                {{ getRiskValue(item) }}
            </span>
        </template>
        <template #cell(updatedAt)="{ item }">
            {{ $format.date(item.updatedAt) }}
        </template>
        <template #cell(action)="{ item }">
            <ButtonIcon size="sm" :to="getItemUrl(item)">
                {{ $t('show') }}
            </ButtonIcon>
        </template>
    </DataTable>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';
    import BadgeBoolean from '@/components/common/BadgeBoolean.vue';
    import BadgeHidden from '@/components/common/BadgeHidden.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import { translateOfferType } from '@/services/offersService';

    export default {
        name: 'Results',
        components: {
            DataTable,
            ImageLoader,
            BadgeBoolean,
            BadgeHidden,
            Tooltip,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        computed: {
            fields() {
                return [
                    { key: 'product', label: this.$t('aggregateOfferId') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'info', label: this.$t('info') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'feed', label: this.$t('feedShop') },
                    { key: 'type', label: this.$t('offerType') },
                    { key: 'price', label: this.$t('price') },
                    { key: 'hidden', label: this.$t('hidden') },
                    { key: 'active', label: this.$t('availability') },
                    { key: 'risk', label: this.$t('riskValue') },
                    { key: 'updatedAt', label: this.$t('updatedAt') },
                    { key: 'action', label: '' },
                ];
            },
            riskLabelsOffer() {
                return {
                    info_similarity: this.$t('riskInfoSimilarity'),
                    name_similarity: this.$t('riskNameSimilarity'),
                    offer_quantity: this.$t('riskOfferQuantity'),
                };
            },
            riskLabelsProduct() {
                return {
                    existing_variant: this.$t('riskExistingVariant'),
                    info_similarity: this.$t('riskInfoSimilarity'),
                    name_similarity: this.$t('riskNameSimilarity'),
                };
            },
        },
        methods: {
            getProductIds(item) {
                const products = Array.isArray(item.products) ? item.products : [];
                return products.map(product => product.productId).filter(Boolean);
            },
            getRiskValue(item) {
                const products = Array.isArray(item.products) ? item.products : [];
                if (!products.length) return '-';
                if (products.length === 1) return products[0].riskScore ?? '-';
                return `${products.length} / ${this.$t('products')}`;
            },
            getTypeLabel(type) {
                return translateOfferType(type);
            },
            getItemUrl(item) {
                return {
                    name: 'OffersModerationDetails',
                    params: { id: item.id, parentUrl: this.$route.fullPath },
                    query: {},
                };
            },
            formatRiskSection(title, data, labels) {
                if (!data || typeof data !== 'object') return '';
                const entries = Object.entries(data).filter(([, value]) => value !== null && value !== undefined);
                if (!entries.length) return '';
                const items = entries.map(([name, value]) => `<li><strong>${labels[name] || name}</strong>: ${value}</li>`);
                return `<div class="mb-2 text-left"><strong>${title}</strong><ul class="pl-3 m-0 d-flex flex-column justify-content-start text-left">${items.join('')}</ul></div>`;
            },
            getRiskHtml(item) {
                const products = Array.isArray(item.products) ? item.products : [];
                if (!products.length) return null;
                const blocks = products.map(product => {
                    const offerSection = this.formatRiskSection(this.$t('offerRiskScores'), product.offerRisks, this.riskLabelsOffer);
                    const productSection = this.formatRiskSection(this.$t('productRiskScores'), product.productRisks, this.riskLabelsProduct);
                    const header = `<div class="mb-1 text-left"><strong>${this.$t('productId')}:</strong> ${product.productId ?? '-'}</div>`;
                    const riskScore = `<div class="mb-2 text-left"><strong>${this.$t('riskScore')}:</strong> ${product.riskScore ?? '-'}</div>`;
                    return `<div class="mb-3 text-left">${header}${riskScore}${offerSection}${productSection}</div>`;
                });
                return `<div class="text-left">${blocks.filter(Boolean).join('')}</div>`;
            },
        },
    };
</script>
